/* eslint-disable no-nested-ternary */
import React from 'react';
import { Loader } from 'react-feather';
import { useNavigate } from 'react-router-dom';

import { ProductListTableProps } from '../../types/productTypes';

const ProductListTable: React.FC<ProductListTableProps> = ({
  products,
  isLoading,
  hasError,
  isEmpty,
  error,
}) => {
  const navigate = useNavigate();

  const moveToDetailPage = (productUuid: string) => {
    navigate(`/product/${productUuid}`);
  };

  const renderTableContent = () => {
    if (isLoading) {
      return (
        <tr>
          <td colSpan={11} className="px-16 py-8 text-left">
            <div className="flex items-center justify-start">
              <Loader className="h-6 w-6 animate-spin text-[#1D1D1D]" />
              <span className="ml-2 text-sm text-[#1D1D1D]">Loading...</span>
            </div>
          </td>
        </tr>
      );
    }

    if (hasError) {
      return (
        <tr>
          <td colSpan={11} className="px-16 py-8 text-left">
            <div className="text-sm text-red-600">
              Failed to load products.
              {error?.message && (
                <div className="mt-1 text-xs text-gray-500">
                  {error.message}
                </div>
              )}
            </div>
          </td>
        </tr>
      );
    }

    if (isEmpty) {
      return (
        <tr>
          <td colSpan={11} className="px-4 py-8 text-center">
            <div className="text-sm text-[#707070]">
              No search results found.
            </div>
          </td>
        </tr>
      );
    }

    return products.map((product) => (
      <tr key={product.optionUuid} className="border-b border-[#DEDEDE]">
        <td className="w-[72px] whitespace-nowrap px-4 py-1.5 text-left align-middle text-sm text-[#1D1D1D]">
          <img
            src={product.thumbnailUrl || ''}
            alt="product"
            className="h-10 w-10 object-contain"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = '';
            }}
          />
        </td>
        <td
          className="w-[560px] cursor-pointer whitespace-nowrap px-4 py-1.5 text-left align-middle text-sm text-[#1D1D1D] hover:underline"
          onClick={() => moveToDetailPage(product.productUuid)}
        >
          {product.title}
        </td>
        <td className="w-40 whitespace-nowrap px-4 py-1.5 text-left align-middle text-sm text-[#1D1D1D]">
          {product.statusId === 1
            ? 'Active'
            : product.statusId === 2
              ? 'Closed'
              : product.statusId === 3
                ? 'Draft'
                : '-'}
        </td>
        <td className="w-40 whitespace-nowrap px-4 py-1.5 text-left align-middle text-sm text-[#1D1D1D]">
          {product.stock}
        </td>
        <td className="w-40 whitespace-nowrap px-4 py-1.5 text-left align-middle text-sm text-[#1D1D1D]">
          {product.optionNameKey1 || '-'}
        </td>
        <td className="w-40 whitespace-nowrap px-4 py-1.5 text-left align-middle text-sm text-[#1D1D1D]">
          {product.optionNameValue1 || '-'}
        </td>
        <td className="w-40 whitespace-nowrap px-4 py-1.5 text-left align-middle text-sm text-[#1D1D1D]">
          {product.optionNameKey2 || '-'}
        </td>
        <td className="w-40 whitespace-nowrap px-4 py-1.5 text-left align-middle text-sm text-[#1D1D1D]">
          {product.optionNameValue2 || '-'}
        </td>
        <td className="w-40 whitespace-nowrap px-4 py-1.5 text-left align-middle text-sm text-[#1D1D1D]">
          {product.weight}
        </td>
        <td className="w-40 whitespace-nowrap px-4 py-1.5 text-left align-middle text-sm text-[#1D1D1D]">
          {product.barcode || '-'}
        </td>
        <td className="w-[200px] whitespace-nowrap px-4 py-1.5 text-left align-middle text-sm text-[#1D1D1D]">
          {product.minimumOrderQuantity}
        </td>
      </tr>
    ));
  };

  return (
    <div className="scrollbar-custom min-h-[calc(100vh-56px)] w-full overflow-auto border border-t-0 border-[#DEDEDE] bg-[#F1F1F1]">
      <table className="min-w-[2072px] table-fixed border-collapse">
        <thead>
          <tr className="bg-[#EFEFEF]">
            <th className="w-[72px] whitespace-nowrap px-4 py-2 text-left align-middle text-sm font-medium text-[#707070]" />
            <th className="w-[560px] whitespace-nowrap px-4 py-2 text-left align-middle text-sm font-medium text-[#707070]">
              Product
            </th>
            <th className="w-40 whitespace-nowrap px-4 py-2 text-left align-middle text-sm font-medium text-[#707070]">
              Status
            </th>
            <th className="w-40 whitespace-nowrap px-4 py-2 text-left align-middle text-sm font-medium text-[#707070]">
              Stock
            </th>
            <th className="w-40 whitespace-nowrap px-4 py-2 text-left align-middle text-sm font-medium text-[#707070]">
              Option Name1
            </th>
            <th className="w-40 whitespace-nowrap px-4 py-2 text-left align-middle text-sm font-medium text-[#707070]">
              Option Value1
            </th>
            <th className="w-40 whitespace-nowrap px-4 py-2 text-left align-middle text-sm font-medium text-[#707070]">
              Option Name2
            </th>
            <th className="w-40 whitespace-nowrap px-4 py-2 text-left align-middle text-sm font-medium text-[#707070]">
              Option Value2
            </th>
            <th className="w-40 whitespace-nowrap px-4 py-2 text-left align-middle text-sm font-medium text-[#707070]">
              Weight
            </th>
            <th className="w-40 whitespace-nowrap px-4 py-2 text-left align-middle text-sm font-medium text-[#707070]">
              Barcode
            </th>
            <th className="w-[200px] whitespace-nowrap px-4 py-2 text-left align-middle text-sm font-medium text-[#707070]">
              Minimum Order Quantity
            </th>
          </tr>
        </thead>
        <tbody className="bg-white">{renderTableContent()}</tbody>
      </table>
    </div>
  );
};

export default ProductListTable;
